// Registry of the integration providers the dashboard fronts.
//
// Providers split into two families that share almost no UI:
//  - `document` providers (Tungsten) push discrete invoice/credit documents,
//    which is what the transactions table renders.
//  - `schedule` providers (Square D, Siemens) publish on a schedule instead —
//    a run batches up POS sales or an inventory snapshot and transmits it — so
//    they get a transmission log rather than a filtered transactions view.
//
// The kind drives routing and navigation, so adding a provider here is enough
// to give it a slug, a sidebar entry and a landing route.

export type ProviderKind = 'document' | 'schedule';

export interface Provider {
  /** URL slug — the `:providerId` route segment. */
  id: string;
  /** Human label shown in the switcher and page headings. */
  label: string;
  /**
   * Value sent as the `provider` query param. Free-form on the backend, so it
   * is tracked separately from the slug rather than derived from it.
   *
   * Only Tungsten's name is confirmed against live data; the schedule
   * providers don't take this param at all — they're addressed by the
   * integration names in `lib/api/transmissions.ts`, which are a closed enum.
   */
  apiName: string;
  kind: ProviderKind;
}

export const PROVIDERS: Provider[] = [
  {id: 'tungsten', label: 'Tungsten', apiName: 'Tungsten', kind: 'document'},
  {id: 'square-d', label: 'Square D', apiName: 'SquareD', kind: 'schedule'},
  {id: 'siemens', label: 'Siemens', apiName: 'Siemens', kind: 'schedule'},
];

export const DEFAULT_PROVIDER = PROVIDERS[0];

export function getProvider(id: string | undefined): Provider | undefined {
  return PROVIDERS.find(p => p.id === id);
}

/**
 * The section a provider lands on. Document and schedule providers have
 * disjoint pages, so switching provider can't preserve the current section.
 */
export function providerHomePath(p: Provider): string {
  return p.kind === 'document'
    ? `/${p.id}/transactions`
    : `/${p.id}/transmissions`;
}

// Which provider family each section belongs to. A section is only reachable
// under a provider of the matching kind.
const SECTION_KIND: Record<string, ProviderKind> = {
  transactions: 'document',
  transmissions: 'schedule',
};

/**
 * Decide where a `/:providerId/...` URL should actually render.
 *
 * Returns `null` to render the requested route, or a path to redirect to.
 * Two URLs can't be served as written: an unknown provider slug, and a section
 * belonging to the other provider family (typed by hand, or a stale bookmark
 * from before a provider switch). Both land on a provider home instead of
 * rendering a page against data that cannot exist.
 */
export function resolveProviderRoute(
  providerId: string | undefined,
  pathname: string
): string | null {
  const provider = getProvider(providerId);
  if (!provider) return providerHomePath(DEFAULT_PROVIDER);

  const section = pathname.split('/')[2] ?? '';
  const requiredKind = SECTION_KIND[section];
  if (requiredKind && requiredKind !== provider.kind) {
    return providerHomePath(provider);
  }
  return null;
}
